import React from 'react';
import { keyBy, merge, values } from 'lodash';
import { useDispatch } from 'react-redux';
import { Grid, LinearProgress, List, ListItem, ListItemButton, Typography } from '@mui/material';

import { type ReportParameter } from './ParametersStep';
import useSettings from '../../hooks/useSettings';
import { executeApi } from '../../Utils';
import Text from '../../Text';

interface TemplateListItemProps {
	report: any;
	onClick: (reportUri: string) => void;
	isSelected: boolean;
	isLast: boolean;
}

interface TemplateStepProps {
	module: string;
	folder: string;
	reportUri?: string;
	setReportUri: React.Dispatch<React.SetStateAction<string | undefined>>;
	defaultParameters: ReportParameter<any>[];
	setParameters: React.Dispatch<React.SetStateAction<any[]>>;
}

function TemplateListItem(props: TemplateListItemProps) {
	return (
		<ListItem key={props.report.uri} divider={!props.isLast} disablePadding disableGutters>
			<ListItemButton selected={props.isSelected} onClick={() => props.onClick(props.report.uri)}>
				<Grid container>
					<Grid size={12}>
						<Typography variant="subtitle2">{props.report.label}</Typography>
					</Grid>
					<Grid size={12}>
						<Text className="text-10" variant="subtitle2" color="text.secondary">
							{props.report.uri}
						</Text>
					</Grid>
					<Grid size={12}>
						<Typography variant="body1">{props.report.description}</Typography>
					</Grid>
				</Grid>
			</ListItemButton>
		</ListItem>
	);
}

export default function TemplateStep(props: TemplateStepProps) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();

	const [standardReports, setStandardReports] = React.useState<any[]>([]);
	const [customReports, setCustomReports] = React.useState<any[]>([]);

	const [isLoadingStandardReports, setIsLoadingStandardReports] = React.useState(false);
	const [isLoadingCustomReports, setIsLoadingCustomReports] = React.useState(false);

	function onItemClick(reportUri: string) {
		if (reportUri !== 'main') {
			props.setReportUri(reportUri);

			if (reportUri !== props.reportUri) {
				executeApi.get({
					url: `/api/reporting/reports/parameters${reportUri}`,
					dispatch,
					successHandler: response =>
						props.setParameters(
							values(merge(keyBy(response.data, 'id'), keyBy(props.defaultParameters, 'id')))
						)
				});
			}
		}
	}

	React.useEffect(() => {
		if (sysSettings.report_standard_root_folder) {
			setIsLoadingStandardReports(true);
			executeApi.get({
				url: `/api/reporting/reports${sysSettings.report_standard_root_folder}/${props.module}/${props.folder}`,
				dispatch,
				successHandler: response => {
					setStandardReports(response.data || []);
					setIsLoadingStandardReports(false);
				},
				errorHandler: () => setIsLoadingStandardReports(false)
			});
		}
	}, [dispatch, props.folder, props.module, sysSettings.report_standard_root_folder]);

	React.useEffect(() => {
		if (sysSettings.report_custom_root_folder) {
			setIsLoadingCustomReports(true);
			executeApi.get({
				url: `/api/reporting/reports${sysSettings.report_custom_root_folder}/${props.module}/${props.folder}`,
				dispatch,
				successHandler: response => {
					setCustomReports(response.data || []);
					setIsLoadingCustomReports(false);
				},
				errorHandler: () => setIsLoadingCustomReports(false)
			});
		}
	}, [dispatch, props.folder, props.module, sysSettings.report_custom_root_folder]);

	const reports = [...standardReports, ...customReports];

	return !isLoadingStandardReports && !isLoadingCustomReports ? (
		reports.length > 0 ? (
			<List className="py-8" disablePadding>
				{reports.map((report: any, index: number) => (
					<TemplateListItem
						key={report.uri}
						report={report}
						onClick={onItemClick}
						isSelected={report.uri === props.reportUri}
						isLast={index === reports.length - 1}
					/>
				))}
			</List>
		) : (
			<Typography className="p-16 italic" align="center" color="textSecondary">
				{t('LIST_NO_ITEMS')}
			</Typography>
		)
	) : (
		<LinearProgress variant="query" />
	);
}
